import React, { useCallback } from 'react';

import { Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { useTooltipInPortal } from '@visx/tooltip';

// Utility function to clamp a number between a min and max value
function clamp({ number, min, max }: { number: number; min: number; max: number }) {
  return Math.max(min, Math.min(number, max));
}

type Margin = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type XYChartTooltipProps = {
  tooltipLeft?: number;
  chartWidth: number;
  chartHeight: number;
  margin: Margin;
  tooltipWidth: number;
  children: React.ReactNode;
};

export const useXYChartTooltipInPortal = () => {
  const { TooltipInPortal, containerRef } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const XYChartTooltipInPortal = useCallback(
    ({ tooltipLeft = 0, chartHeight, margin, children }: Omit<XYChartTooltipProps, 'chartWidth' | 'tooltipWidth'>) => {
      const absoluteTooltipX = margin.left + tooltipLeft;

      return (
        <TooltipInPortal
          key={Math.random()}
          top={chartHeight - margin.bottom}
          left={absoluteTooltipX}
          offsetTop={0}
          offsetLeft={-28}
          unstyled={true}
          className={css({ position: 'absolute', pointerEvents: 'none', zIndex: 1 })}
        >
          <Text variant="text25" css={{ textWrap: 'nowrap' }}>
            {children}
          </Text>
        </TooltipInPortal>
      );
    },
    [TooltipInPortal]
  );

  const ClampedXYChartTooltipInPortal = useCallback(
    ({ tooltipLeft = 0, margin, children, tooltipWidth, chartWidth }: Omit<XYChartTooltipProps, 'chartHeight'>) => {
      const absoluteTooltipX = margin.left + tooltipLeft;
      const TOOLTIP_OVERHANG = 8;

      return (
        <TooltipInPortal
          className={css({
            backgroundColor: 'background.surface',
            position: 'absolute',
            pointerEvents: 'none',
            padding: 'space.150',
            borderRadius: 'medium',
            border: 'solid token(borderWidths.thin) token(colors.border.strong)',
            translate: '0% -100%', // Position above the cursor line
          })}
          style={{ width: `${tooltipWidth}px` }}
          top={10}
          left={clamp({
            number: absoluteTooltipX - tooltipWidth / 2,
            min: margin.left - TOOLTIP_OVERHANG, // Allow slight overlap with margin
            max: chartWidth - margin.right - tooltipWidth + TOOLTIP_OVERHANG, // Adjust max based on chart width and margins
          })}
          offsetLeft={0}
          offsetTop={0}
        >
          {children}
        </TooltipInPortal>
      );
    },
    [TooltipInPortal]
  );
  return {
    containerRef,
    XYChartTooltipInPortal,
    ClampedXYChartTooltipInPortal,
  };
};
